// Display exercises and utilize exercise sets react component
import React, { useState } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import ExerciseSets from "../exercise_sets/ExerciseSets";
import {
  useExercises,
  useExerciseTemplates,
  useUpdateExercise,
  useDeleteExercise,
  useUpdateExercisePosition,
  useExerciseHistory,
} from "./exercises_api";

// Component to display exercise history
const ExerciseHistory = ({ exerciseTemplateId, currentWorkoutId }) => {
  const {
    data: history,
    isLoading,
    isError,
  } = useExerciseHistory(exerciseTemplateId);

  if (isLoading) return <div className="text-muted">Loading history...</div>;
  if (isError) return <div className="text-danger">Error loading history</div>;

  // Filter out the current workout from history
  const filteredHistory = history
    ? history.filter((exercise) => exercise.workout.id !== currentWorkoutId)
    : [];

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const renderSets = (exerciseSets) => {
    if (!exerciseSets || exerciseSets.length === 0) {
      return <span className="text-muted">No sets recorded</span>;
    }

    return (
      <div className="small">
        {exerciseSets
          .sort((a, b) => a.position - b.position)
          .map((set, index) => (
            <div key={index} className="mb-1">
              {set.duration
                ? `${set.duration} seconds`
                : `${set.reps} reps @ ${set.weight} lbs`}
            </div>
          ))}
      </div>
    );
  };

  return (
    <div className="exercise-history mt-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h6 className="mb-0">Exercise History</h6>
        <a
          href={`/exercise_templates/${exerciseTemplateId}`}
          className="btn btn-sm btn-outline-primary"
          target="_blank"
          rel="noopener noreferrer"
        >
          <i className="bi bi-box-arrow-up-right"></i> View Details
        </a>
      </div>
      {filteredHistory && filteredHistory.length > 0 ? (
        <div className="table-responsive">
          <table className="table table-sm table-striped">
            <thead>
              <tr>
                <th>Date</th>
                <th>Gym</th>
                <th>Sets</th>
              </tr>
            </thead>
            <tbody>
              {filteredHistory.map((exercise, index) => (
                <tr key={index}>
                  <td>{formatDate(exercise.workout.date)}</td>
                  <td>
                    {exercise.workout.gym ? (
                      <span className="text-primary">
                        {exercise.workout.gym.name}
                      </span>
                    ) : (
                      <span className="text-muted">No gym</span>
                    )}
                  </td>
                  <td>{renderSets(exercise.exercise_sets)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-muted mb-0">No history for this exercise yet.</p>
      )}
    </div>
  );
};

// SortableExerciseCard component for drag-and-drop functionality
const SortableExerciseCard = ({ id, exerciseData, workoutId }) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const dragHandleStyle = {
    touchAction: "none",
    cursor: "grab",
  };

  const [isContentVisible, setIsContentVisible] = useState(true);
  const [isHistoryVisible, setIsHistoryVisible] = useState(false);
  const updateExerciseMutation = useUpdateExercise();
  const deleteExerciseMutation = useDeleteExercise();
  const { data: exerciseTemplates } = useExerciseTemplates();

  const toggleContent = () => {
    setIsContentVisible(!isContentVisible);
  };

  const toggleHistory = () => {
    setIsHistoryVisible(!isHistoryVisible);
  };

  const preventToggle = (e) => {
    e.stopPropagation();
  };

  const handleUpdateExercise = (e) => {
    e.preventDefault();
    const exerciseTemplateId = e.target.exercise_template_id.value;

    updateExerciseMutation.mutate({
      workoutId,
      exerciseId: id,
      exerciseTemplateId,
    });

    // Close the modal
    const modal = document.getElementById(`editExerciseModal${id}`);
    const modalBackdrop = document.querySelector(".modal-backdrop");

    if (modal) {
      modal.classList.remove("show");
      modal.style.display = "none";
      document.body.classList.remove("modal-open");
    }

    if (modalBackdrop) {
      modalBackdrop.remove();
    }
  };

  const handleDeleteExercise = () => {
    if (confirm("Are you sure you want to delete this exercise?")) {
      deleteExerciseMutation.mutate({
        workoutId,
        exerciseId: id,
      });
    }
  };

  // Function to group exercise templates by muscle
  const groupTemplatesByMuscle = () => {
    if (!exerciseTemplates) return {};

    const groups = {};
    exerciseTemplates.forEach((template) => {
      const muscleName = template.muscle?.name || "Other";
      if (!groups[muscleName]) {
        groups[muscleName] = [];
      }
      groups[muscleName].push(template);
    });

    return groups;
  };

  return (
    <div className="col-md-6 mb-3" ref={setNodeRef} style={style} data-id={id}>
      <div className="card h-100">
        <div
          className="card-header bg-light d-flex justify-content-between align-items-center"
          onClick={toggleContent}
        >
          <div className="d-flex align-items-center">
            <div
              className="drag-handle me-2"
              {...attributes}
              {...listeners}
              onClick={preventToggle}
              style={dragHandleStyle}
            >
              <i className="bi bi-grip-vertical fs-5 text-secondary"></i>
            </div>
            <h5 className="mb-0">
              {exerciseData.exercise_template?.name}
              {exerciseData.exercise_template?.muscle && (
                <span className="badge bg-primary ms-2">
                  {exerciseData.exercise_template.muscle.name
                    .charAt(0)
                    .toUpperCase() +
                    exerciseData.exercise_template.muscle.name.slice(1)}
                </span>
              )}
              {exerciseData.exercise_template?.secondary_muscles?.map(
                (muscle) => (
                  <span key={muscle.id} className="badge bg-secondary ms-2">
                    {muscle.name.charAt(0).toUpperCase() + muscle.name.slice(1)}
                  </span>
                )
              )}
            </h5>
          </div>
          <div>
            <span className="badge bg-secondary">
              {exerciseData.exercise_sets?.length}{" "}
              {exerciseData.exercise_sets?.length === 1 ? "set" : "sets"}
            </span>
            <i
              className={`bi bi-chevron-${
                isContentVisible ? "up" : "down"
              } ms-2`}
            ></i>
          </div>
        </div>

        {isContentVisible && (
          <div className="card-body">
            <div className="mb-3">
              <ExerciseSets exerciseId={id} />
            </div>

            <div className="d-flex justify-content-end gap-2">
              <button
                type="button"
                className="btn btn-info btn-sm"
                onClick={toggleHistory}
              >
                <i className="bi bi-clock-history"></i> History
              </button>

              <button
                type="button"
                className="btn btn-primary btn-sm"
                data-bs-toggle="modal"
                data-bs-target={`#editExerciseModal${id}`}
              >
                <i className="bi bi-pencil"></i> Edit Exercise
              </button>

              <button
                type="button"
                className="btn btn-danger btn-sm"
                onClick={handleDeleteExercise}
              >
                <i className="bi bi-trash"></i> Delete Exercise
              </button>
            </div>

            {isHistoryVisible && (
              <ExerciseHistory
                exerciseTemplateId={exerciseData.exercise_template?.id}
                currentWorkoutId={workoutId}
              />
            )}
          </div>
        )}

        {/* Edit Exercise Modal */}
        <div
          className="modal fade"
          id={`editExerciseModal${id}`}
          tabIndex="-1"
          aria-labelledby={`editExerciseModalLabel${id}`}
          aria-hidden="true"
        >
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title" id={`editExerciseModalLabel${id}`}>
                  Edit Exercise
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body">
                <form onSubmit={handleUpdateExercise}>
                  <div className="mb-3">
                    <label
                      htmlFor="exercise_template_id"
                      className="form-label"
                    >
                      Exercise
                    </label>
                    <select
                      id="exercise_template_id"
                      name="exercise_template_id"
                      className="form-select"
                      defaultValue={exerciseData.exercise_template?.id}
                      required
                    >
                      {exerciseTemplates &&
                        Object.entries(groupTemplatesByMuscle()).map(
                          ([muscleName, templates]) => (
                            <optgroup
                              label={
                                muscleName.charAt(0).toUpperCase() +
                                muscleName.slice(1)
                              }
                              key={muscleName}
                            >
                              {templates.map((template) => (
                                <option key={template.id} value={template.id}>
                                  {template.name}
                                </option>
                              ))}
                            </optgroup>
                          )
                        )}
                    </select>
                  </div>

                  <div className="d-grid">
                    <button type="submit" className="btn btn-primary">
                      Update Exercise
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Exercises component
const Exercises = ({ workoutId }) => {
  const { data: exercises, isLoading, isError } = useExercises(workoutId);
  const updatePositionMutation = useUpdateExercisePosition();

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 10,
        delay: 100,
        tolerance: 5,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        distance: 10,
        delay: 100,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor)
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = exercises.findIndex(
        (exercise) => exercise.id === active.id
      );
      const newIndex = exercises.findIndex(
        (exercise) => exercise.id === over.id
      );

      updatePositionMutation.mutate({
        exerciseId: active.id,
        newPosition: newIndex,
      });
    }
  };

  if (isLoading) return <div>Loading exercises...</div>;
  if (isError) return <div>Error loading exercises</div>;

  return (
    <div className="card mb-4">
      <div className="card-header d-flex justify-content-between align-items-center">
        <h2 className="card-title mb-0">Exercises</h2>
        <small className="text-muted">
          <i className="bi bi-info-circle"></i> Drag exercises to reorder
        </small>
      </div>
      <div className="card-body">
        {exercises?.length > 0 ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={exercises.map((exercise) => exercise.id)}
              strategy={verticalListSortingStrategy}
            >
              <div
                className="row"
                data-sortable-url="/exercises/update_position"
              >
                {exercises.map((exercise) => (
                  <SortableExerciseCard
                    key={exercise.id}
                    id={exercise.id}
                    exerciseData={exercise}
                    workoutId={workoutId}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        ) : (
          <p className="text-muted mb-0">No exercises added yet.</p>
        )}
      </div>
    </div>
  );
};

export default Exercises;
